import { WebchatClientState } from '@botpress/webchat'
import axios from 'axios'
import cookie from 'js-cookie'
import { WEBCHAT_READY_EVENT } from '~/providers/WebchatScriptProvider'
import { config, PAT_KEY } from '~/shared'

// Default CDN configuration
export const SUPPORT_BOT_CLIENT_ID = '3acb66c7-9afe-4643-b58d-3522ed11d72a' // If you change this make sure to also change it in the studio !!
const DEFAULT_BOT_ID = '25d6fc2e-6603-4df9-bf53-dd1e324b4fac'

// New CDN configuration (desk)
const DESK_BOT_CLIENT_ID = import.meta.env.VITE_DESK_BOT_CLIENT_ID || 'd68af245-a3aa-4c94-941d-03efee7bb07e'
const DESK_BOT_ID = import.meta.env.VITE_DESK_BOT_ID || '2d70c4a0-6532-4919-9d42-0f9662acd795'

const getCurrentWebchatState = (clientId: string): WebchatClientState | null => {
  try {
    const text = localStorage.getItem(`bp-webchat-${clientId}`)
    if (!text) {
      return null
    }
    return JSON.parse(text) as WebchatClientState
  } catch {
    // eslint-disable-next-line no-console
    console.warn('Could not get webchat state from local storage')
  }

  return null
}

export const isUsingDeskWebchat = (): boolean => {
  const script = document.getElementById('botpress-webchat-inject') as HTMLScriptElement | null
  return script?.src.includes('/desk/') ?? false
}

export async function fetchWebchatToken(workspaceId: string): Promise<string | null> {
  try {
    const cloudPat = config.usePat ? config.pat ?? cookie.get(PAT_KEY) : undefined

    const { data } = await axios.get<{ token: string }>(`${config.studioLambdaApiUrl}/webchat-token`, {
      headers: {
        'x-botpress-workspace-id': workspaceId,
        authorization: config.usePat ? `Bearer ${cloudPat}` : undefined,
      },
      withCredentials: !config.usePat,
    })

    return data.token ?? null
  } catch {
    // eslint-disable-next-line no-console
    console.warn('[support-webchat] Could not fetch webchat identity token')
    return null
  }
}

const performInit = (workspaceId: string) => {
  const cookieKey = `bp-webchat-state-${workspaceId}`
  if (!window.botpress) {
    // eslint-disable-next-line no-console
    console.warn('window.botpress not available for initialization')
    return
  }

  // Determine which configuration to use based on whether desk webchat is loaded
  const isDeskWebchat = isUsingDeskWebchat()
  const botId = isDeskWebchat ? DESK_BOT_ID : DEFAULT_BOT_ID
  const clientId = isDeskWebchat ? DESK_BOT_CLIENT_ID : SUPPORT_BOT_CLIENT_ID

  const savedWebchatState = cookie.get(cookieKey)

  if (savedWebchatState) {
    localStorage.setItem(`bp-webchat-${clientId}`, savedWebchatState)
  }

  window.botpress.on('conversation', () => {
    const currentWebchatState = getCurrentWebchatState(clientId)

    if (currentWebchatState) {
      cookie.set(cookieKey, JSON.stringify(currentWebchatState), {
        domain: location.hostname.endsWith('botpress.cloud') ? 'botpress.cloud' : location.hostname,
        expires: 30,
      })
    }
  })

  window.botpress.init({
    botId,
    configuration: {
      version: 'v2',
      composerPlaceholder: 'We typically reply within a day.',
      botName: 'Help Desk',
      botAvatar: 'https://files.bpcontent.cloud/2025/09/04/17/20250904173912-R4IJO1FR.svg',
      botDescription: 'For all of your bot-building queries.',
      website: {},
      email: {},
      phone: {},
      termsOfService: {},
      privacyPolicy: {},
      color: '#3276EA',
      variant: 'solid',
      headerVariant: 'glass',
      themeMode: 'light',
      fontFamily: 'Inter',
      radius: 4,
      feedbackEnabled: false,
      footer: '[⚡ by Botpress](https://botpress.com/?from=webchat)',
      allowFileUpload: true,
      soundEnabled: false,
      toggleChatId: 'bp-toggle-chat',
      proactiveMessageEnabled: false,
      proactiveBubbleMessage: 'Hi! 👋 Need help?',
      proactiveBubbleTriggerType: 'afterDelay',
      proactiveBubbleDelayTime: 10,
      conversationHistory: true,
    },
    clientId,
  })
}

export const initSupportWebchat = (workspaceId: string) => {
  // If window.botpress is already available, init immediately
  if (window.botpress) {
    performInit(workspaceId)
    return
  }

  // Otherwise, wait for the webchat-ready event
  const handleWebchatReady = () => {
    performInit(workspaceId)
    // Clean up the event listener after initialization
    window.removeEventListener(WEBCHAT_READY_EVENT, handleWebchatReady)
  }

  window.addEventListener(WEBCHAT_READY_EVENT, handleWebchatReady)
}
